import Cookies from 'js-cookie';
import apiAxios from './apiAxios';
import authApi from './auth';
import { AuthToken } from '../types/user';
import { logger, logOnError } from '../utility/errors';

export default class JwtService {
  accessTokenKey = 'accessToken';
  refreshTokenKey = 'refreshToken';

  refreshTokenIssueDate = 'refreshTokenIssueDate';
  refreshTokenExpirationDate = 'refreshTokenExpirationDate';

  isAlreadyFetchingAccessToken = false;

  accessTokenSubs: { (accessToken: string): void }[] = [];
  unauthorizedSubs: { (): void }[] = [];

  constructor() {
    apiAxios.interceptors.request.use(
      (config) => {
        const accessToken = this.getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    apiAxios.interceptors.response.use(
      (response) => response,
      (error) => {
        const { config, response } = error;
        if (response?.status !== 401) {
          return Promise.reject(error);
        }
        if (!this.isAlreadyFetchingAccessToken) {
          const refreshToken = this.getRefreshToken();
          if (!refreshToken) {
            logger.info(
              `Refresh token not available on request. Last token issue: ${this.getRefreshTokenIssueDate()}. Last token expiration: ${this.getRefreshTokenExpirationDate()}`,
              'unauthorized'
            );
            this.onUnauthorized();
            return Promise.reject(error);
          }

          this.isAlreadyFetchingAccessToken = true;
          authApi
            .refreshToken(refreshToken)
            .then((auth) => {
              this.setAuth(auth);
              this.onAccessTokenFetched(auth.accessToken);
            })
            .catch((error) => {
              logOnError(new Error(`Failed token refresh: ${error.message}`));
              this.onUnauthorized();
              throw error;
            })
            .finally(() => {
              this.isAlreadyFetchingAccessToken = false;
            });
        }
        return new Promise((resolve) => {
          this.addAccessTokenSubscriber((accessToken) => {
            config.headers.Authorization = `Bearer ${accessToken}`;
            resolve(apiAxios(config));
          });
        });
      }
    );
  }

  onAccessTokenFetched(accessToken: string) {
    this.accessTokenSubs = this.accessTokenSubs.filter((callback) =>
      callback(accessToken)
    );
  }

  addAccessTokenSubscriber(callback: { (accessToken: string): void }) {
    this.accessTokenSubs.push(callback);
  }

  onUnauthorized() {
    this.unauthorizedSubs = this.unauthorizedSubs.filter((callback) =>
      callback()
    );
  }

  addOnUnauthorizedSubscriber(callback: { (): void }) {
    this.unauthorizedSubs.push(callback);
  }

  getAccessToken() {
    return Cookies.get(this.accessTokenKey);
  }

  getRefreshToken() {
    return Cookies.get(this.refreshTokenKey);
  }

  getRefreshTokenIssueDate(): string {
    return localStorage.getItem(this.refreshTokenIssueDate) || '';
  }

  getRefreshTokenExpirationDate(): string {
    return localStorage.getItem(this.refreshTokenExpirationDate) || '';
  }

  setAuth(auth: AuthToken) {
    const accessTokenExpires = new Date(auth.expiresAt);
    const refreshTokenExpires = new Date(auth.refreshTokenExpiresAt);
    Cookies.set(this.accessTokenKey, auth.accessToken, {
      expires: accessTokenExpires,
    });
    Cookies.set(this.refreshTokenKey, auth.refreshToken, {
      expires: refreshTokenExpires,
    });
    localStorage.setItem(this.refreshTokenIssueDate, new Date().toISOString());
    localStorage.setItem(
      this.refreshTokenExpirationDate,
      refreshTokenExpires.toISOString()
    );
  }

  clear() {
    Cookies.remove(this.accessTokenKey);
    Cookies.remove(this.refreshTokenKey);
    localStorage.removeItem(this.refreshTokenIssueDate);
    localStorage.removeItem(this.refreshTokenExpirationDate);
  }

  hasAuth() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken && !!this.getRefreshTokenIssueDate()) {
      logger.info(
        `Refresh token not available on check. Last token issue: ${this.getRefreshTokenIssueDate()}. Last token expiration: ${this.getRefreshTokenExpirationDate()}`,
        'unauthorized'
      );
    }
    return !!this.getAccessToken() || !!refreshToken;
  }
}
